import { useState } from "react";
import { 
  X, FileText, FolderOpen, Clock, CheckCircle, AlertTriangle,
  Search, Filter, RefreshCw, ChevronRight, Map, Users, Calendar,
  BarChart2, ArrowLeft
} from "lucide-react";
import { ParcoursClientParchemin, AnalyseDossiersBloques } from "./components/telepro";
import { CalendrierButton } from './components/calendrier/CalendrierButton';
import PipelineWidget from "./components/PipelineWidget";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

export default function AdministratifDashboard({ onBack }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [showParcoursClient, setShowParcoursClient] = useState(false);
  const [showDossiersBloques, setShowDossiersBloques] = useState(false);

  // Si on affiche Parcours Client
  if (showParcoursClient) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => setShowParcoursClient(false)}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'Administratif
          </button>
          <ParcoursClientParchemin />
        </div>
      </div>
    );
  }

  // Si on affiche Dossiers Bloqués
  if (showDossiersBloques) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => setShowDossiersBloques(false)}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'Administratif
          </button>
          <AnalyseDossiersBloques />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-950/80 border-b border-slate-800 px-6 py-4 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-gray-600 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">📋 Administratif</h1>
                <p className="text-sm text-slate-400">Gestion des dossiers</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CalendrierButton />
            <button
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Widget Pipeline - Où en est mon client ? */}
        <div className="mb-8">
          <PipelineWidget compact={true} onClientClick={(rowNumber) => {
            console.log("Client clicked:", rowNumber);
          }} />
        </div>

        {/* Cards principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Parcours Client */}
          <button
            onClick={() => setShowParcoursClient(true)}
            data-testid="parcours-client-btn"
            className="bg-gradient-to-br from-amber-900/50 to-orange-900/50 border border-amber-500/30 rounded-xl p-6 hover:border-amber-400 hover:shadow-amber-500/10 hover:shadow-lg transition-all text-left group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Map className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">📜 Parcours Client</h3>
                <p className="text-amber-300 text-sm">Suivi étape par étape</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              Visualisez le parcours complet d'un client depuis la prise de contact jusqu'à la fin des travaux.
            </p>
            <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
              <span>Accéder</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>

          {/* Dossiers Bloqués */}
          <button
            onClick={() => setShowDossiersBloques(true)}
            data-testid="dossiers-bloques-btn"
            className="bg-gradient-to-br from-red-900/50 to-rose-900/50 border border-red-500/30 rounded-xl p-6 hover:border-red-400 hover:shadow-red-500/10 hover:shadow-lg transition-all text-left group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">🚨 Dossiers Bloqués</h3>
                <p className="text-red-300 text-sm">Actions requises</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              Identifiez et débloquez les dossiers qui nécessitent une intervention urgente.
            </p>
            <div className="mt-4 flex items-center gap-2 text-red-400 text-sm">
              <span>Voir les dossiers</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>

          {/* Gestion Documentaire */}
          <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 border border-slate-600/30 rounded-xl p-6 text-left opacity-60">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-gray-600 rounded-xl flex items-center justify-center">
                <FolderOpen className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">📁 Documents</h3>
                <p className="text-slate-400 text-sm">Bientôt disponible</p>
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              Gestion centralisée des documents administratifs des dossiers clients.
            </p>
            <div className="mt-4 flex items-center gap-2 text-slate-500 text-sm">
              <span>En développement</span>
              <Clock className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Section Stats rapides */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-slate-400" />
            Aperçu rapide
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">--</p>
              <p className="text-sm text-slate-400">Dossiers en cours</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">--</p>
              <p className="text-sm text-slate-400">En attente</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-400">--</p>
              <p className="text-sm text-slate-400">Bloqués</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-400">--</p>
              <p className="text-sm text-slate-400">Terminés ce mois</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4 text-center">
            Les statistiques seront alimentées prochainement
          </p>
        </div>
      </main>
    </div>
  );
}
